import { map, uniqueId, some, includes } from 'lodash';
import wImg from '../assets/W.svg';
import tkImg from '../assets/Tk.svg';
import coImg from '../assets/Co.svg';

import BFullImg from '../assets/shot-images/B.jpg';
import BlFullImg from '../assets/shot-images/Bl.jpg';
import BsFullImg from '../assets/shot-images/Bs.jpg';
import CrFullImg from '../assets/shot-images/Cr.jpg';
import DFullImg from '../assets/shot-images/D.jpg';
import EuFullImg from '../assets/shot-images/Eu.jpg';
import FFullImg from '../assets/shot-images/F.jpg';
import FaFullImg from '../assets/shot-images/Fa.jpg';
import FrFullImg from '../assets/shot-images/Fr.jpg';
import MFullImg from '../assets/shot-images/M.jpg';
import ObFullImg from '../assets/shot-images/Ob.jpg';
import PfFullImg from '../assets/shot-images/Pf.jpg';
import BqFullImg from '../assets/shot-images/x-Bq.jpg';
import DmFullImg from '../assets/shot-images/x-Dm.jpg';
import OfFullImg from '../assets/shot-images/x-Of.jpg';
import PtFullImg from '../assets/shot-images/x-Pt.jpg';
import ShFullImg from '../assets/shot-images/x-Sh.jpg';
import SjFullImg from '../assets/shot-images/x-Sj.jpg';
import TmFullImg from '../assets/shot-images/x-Tm.jpg';
import UuFullImg from '../assets/shot-images/x-Uu.jpg';

export interface PeriodicElement {
  fullname: string;
  element: string;
  row?: number;
  column?: number;
  colorClass: string;
  shotType: string;
  description: string;
  unlocker: boolean;
  activeUnlocker?: boolean;
  diagram?: string;
  shotImg?: string;
}

export interface BonusPeriodicElement extends PeriodicElement {
  prereqElements: string[];
  unlockProgress?: number;
  color?: string;
}

const baseElements = [
  // row 1
  {
    element: 'A',
    shotType: 'Hard',
    fullname: 'And-one shot',
    row: 1,
    column: 1,
    colorClass: 'mint',
    description:
      "An extremely malleable shot, it's flexible enough to change mid-shot without breaking on contact.",
  },
  {
    element: 'Ao',
    shotType: 'Near basket',
    fullname: 'Alley oop',
    row: 1,
    column: 11,
    colorClass: 'orangish',
    description:
      'An alley oop is a chain reaction that generally starts with a thermodynamic pass and ends with an aerodynamic dunk (D).',
  },

  // row 2
  {
    element: 'Bl',
    shotType: 'Move',
    shotImg: BlFullImg,
    fullname: 'Between the legs dunk',
    row: 2,
    column: 1,
    colorClass: 'blue',
    description:
      "A molecule of half dunk, half Vinsanity. It's over, let's go home ladies and gentlemen.",
  },
  {
    element: 'Be',
    shotType: 'Move',
    fullname: 'Behind the back shot',
    row: 2,
    column: 2,
    colorClass: 'grey',
    description:
      'The ball goes into an atomic orbit around and behind the back before release.',
  },

  {
    element: 'Dc',
    shotType: 'Hard',
    fullname: 'Double clutch shot',
    row: 2,
    column: 8,
    colorClass: 'mint',
    description:
      '"Double clutching" is to change a shot\'s position mid chemical reaction, making it harder to block.',
  },
  {
    element: 'Hc',
    shotType: 'Hard',
    fullname: 'Half court shot',
    row: 2,
    column: 9,
    colorClass: 'mint',
    description:
      'Product of a chemical reaction of the three (Te). It can be accelerated by a catalyst of a shot clock running out.',
  },
  {
    element: 'Bd',
    shotType: 'Near basket',
    fullname: 'Backdoor shot',
    row: 2,
    column: 10,
    colorClass: 'orangish',
    description:
      'The Kinetic Theory of Basketball states that the backdoor shot occurs after a rapid motion towards the basket, moving parallel to the baseline off the ball.',
  },
  {
    element: 'D',
    shotType: 'Near basket',
    shotImg: DFullImg,
    fullname: 'Dunk',
    row: 2,
    column: 11,
    colorClass: 'orangish',
    description:
      "Different dunks have different chemical characteristics; many are powerful, some are disrespectful, but only Dr. J's are graceful.",
  },

  // row 3
  {
    element: 'Cr',
    shotType: 'Move',
    fullname: 'Crossover shot',
    row: 3,
    column: 1,
    colorClass: 'blue',
    shotImg: CrFullImg,
    description:
      "As the defender's ankles undergo nuclear fission, immediately go up for a shot.",
  },
  {
    element: 'Ds',
    shotType: 'Move',
    fullname: 'Drop step shot',
    row: 3,
    column: 2,
    colorClass: 'grey',
    description:
      'The chemical formula of a drop step shot: while posting up, move the non-pivot foot back and use that foot to spin off the defender for an open shot.',
  },

  {
    element: 'Hv',
    shotType: 'Hard',
    fullname: 'Heave',
    row: 3,
    column: 8,
    colorClass: 'mint',
    description:
      'Any shot beyond half court is classified as a heave. Unless the player\'s first name rhymes with "chef" and their last name with "radioactive energy".',
  },
  {
    element: 'Oh',
    shotType: 'Hard',
    fullname: 'Off-hand shot',
    row: 3,
    column: 9,
    colorClass: 'mint',
    description:
      'While the discovery of this shot has benefited ambidextrous players like Mike Conley, it has also made numerous shot scientists to wonder if Ben Simmons is shooting with the wrong hand.',
  },
  {
    element: 'F',
    shotType: 'Near basket',
    shotImg: FFullImg,
    fullname: 'Floater',
    row: 3,
    column: 10,
    colorClass: 'orangish',
    description:
      "This arching shot is provided enough activation energy to miss the defender's outstretched arms. It is similar to the hook shot (H) although taken with the body square to the basket.",
  },
  {
    element: 'Fr',
    shotType: 'Near basket',
    shotImg: FrFullImg,
    fullname: 'Finger roll',
    row: 3,
    column: 11,
    colorClass: 'orangish',
    description:
      'Discovered by George "The Iceman" Gervin, this shot\'s freezing point is 44°.',
  },

  // row 4
  {
    element: 'Eu',
    shotType: 'Move',
    shotImg: EuFullImg,
    fullname: 'Euro step shot',
    row: 4,
    column: 1,
    colorClass: 'blue',
    description:
      'Much like the shot he discovered, Manu Ginobili has been euro stepping father time.',
  },
  {
    element: 'Hs',
    shotType: 'Move',
    fullname: 'Hop step shot',
    row: 4,
    column: 2,
    colorClass: 'grey',
    description:
      'The chemical formula of a hop step shot: gather dribble, hop with both feet at the same time and promptly go up for a shot. Optional: take the ball over the defender\'s head while hopping (aka Dwyane Wade\'s "windmill crossover").',
  },
  {
    element: 'B',
    shotType: 'General',
    shotImg: BFullImg,
    fullname: 'Bank shot',
    row: 4,
    column: 3,
    colorClass: 'yellow',
    description:
      'Considered the most fundamentally sound shot ever discovered, its nucleus contains 21 neutrons.',
  },
  {
    element: 'Ba',
    shotType: 'General',
    fullname: 'Baseline shot',
    row: 4,
    column: 4,
    colorClass: 'yellow',
    description:
      'CAUTION: Take this shot with extreme care. Breaking of chemical bond may occur when stepping out of bounds.',
  },
  {
    element: 'C',
    shotType: 'General',
    fullname: 'Cutting shot',
    row: 4,
    column: 5,
    colorClass: 'yellow',
    description:
      'The Kinetic Theory of Basketball states that the cutting shot occurs after a rapid motion towards the basket off the ball.',
  },
  {
    element: 'Cs',
    shotType: 'General',
    fullname: 'Catch and shoot shot',
    row: 4,
    column: 6,
    colorClass: 'yellow',
    description:
      'A conductive shot, it transfers the heat of the ball from the passer directly to the basket without much movement. Also known as a spot up shot (Sp).',
  },
  {
    element: 'Co',
    shotType: 'Long range',
    fullname: 'Corner shot',
    diagram: coImg,
    row: 4,
    column: 7,
    colorClass: 'green',
    description:
      "This shot's electron is positioned parallel to the nucleus baseline as outlined in the diagram below.",
  },
  {
    element: 'Ol',
    shotType: 'Hard',
    fullname: 'One-legged shot',
    row: 4,
    column: 8,
    colorClass: 'mint',
    description:
      'A shot with an atomic mass so light it can be done on one leg.',
  },
  {
    element: 'Wf',
    shotType: 'Hard',
    fullname: 'Wrong-footed shot',
    row: 4,
    column: 9,
    colorClass: 'mint',
    description:
      'Sharing similar physical properties to an off-hand shot (Oh) and one-legged shot (Ol), this shot is released while jumping off the opposite foot.',
  },
  {
    element: 'H',
    shotType: 'Near basket',
    fullname: 'Hook shot',
    row: 4,
    column: 10,
    colorClass: 'orangish',
    description:
      "This arching shot is provided enough activation energy to miss the defender's outstretched arms. It is similar to the floater (F) although taken without being square to the basket.",
  },
  {
    element: 'L',
    shotType: 'Near basket',
    fullname: 'Layup',
    row: 4,
    column: 11,
    colorClass: 'orangish',
    description:
      'Considered at the time of discovery as the most basic shot in basketball, the layup has gone through a number of chemical transformations such as the finger roll (Fr) and the reverse layup (R).',
  },

  // row 5
  {
    element: 'Fa',
    shotType: 'Move',
    shotImg: FaFullImg,
    fullname: 'Fadeaway shot',
    row: 5,
    column: 1,
    colorClass: 'blue',
    description:
      'The only shot ever recorded in history to have changed its atomic number from 8 to 24. There are old tales of shot chemists shouting "Kobe!" when this happened.',
  },
  {
    element: 'Js',
    shotType: 'Move',
    fullname: 'Jab step shot',
    row: 5,
    column: 2,
    colorClass: 'grey',
    description:
      'The chemical formula of a jab step shot: "jab" the non-pivot foot forward or sideways to test the defender\'s defensive scheme or make them lose balance before a jump shot (J).',
  },
  {
    element: 'Dr',
    shotType: 'General',
    fullname: 'Driving shot',
    row: 5,
    column: 3,
    colorClass: 'yellow',
    description:
      'The Kinetic Theory of Basketball states that the driving shot occurs in a rapid motion towards the basket with the ball.',
  },
  {
    element: 'Fb',
    shotType: 'General',
    fullname: 'Fastbreak shot',
    row: 5,
    column: 4,
    colorClass: 'yellow',
    description:
      'The Kinetic Theory of Basketball states that the fastbreak shot occurs in a rapid motion from one end of the court to the other.',
  },
  {
    element: 'Ft',
    shotType: 'General',
    fullname: 'Free throw',
    row: 5,
    column: 5,
    colorClass: 'yellow',
    description:
      'A free throw is a subatomic shot made of one proton with a positive charge of +1 point.',
  },
  {
    element: 'I',
    shotType: 'General',
    fullname: 'Isolation shot',
    row: 5,
    column: 6,
    colorClass: 'yellow',
    description:
      "An isolation shot has the chemical property of a noble gas; it doesn't play with others.",
  },
  {
    element: 'Te',
    fullname: 'Three',
    row: 5,
    column: 7,
    colorClass: 'green',
    description:
      'A highly unstable shot, its boiling point fluctuates based on range. The further away from the basket, the hotter it is.',
    shotType: 'Long range',
  },
  {
    element: 'Bz',
    shotType: 'Very hard',
    fullname: 'Buzzer beater',
    row: 5,
    column: 8,
    colorClass: 'peachish',
    description:
      'When taken at just the right time, this shot emits radiation of an electronic buzzer sound.',
  },
  {
    element: 'Gt',
    shotType: 'Very hard',
    fullname: 'Game tying shot',
    row: 5,
    column: 9,
    colorClass: 'peachish',
    description:
      'This particular shot is taken to push the game into chemical equilibrium.',
  },
  {
    element: 'Pa',
    shotType: 'Near basket',
    fullname: 'Paint shot',
    row: 5,
    column: 10,
    colorClass: 'orangish',
    description:
      'A paint shot is taken close to the basket with varying lengths of chemical bonds. The shorter the bond, the higher stability and FG% it has.',
  },
  {
    element: 'Pb',
    shotType: 'Near basket',
    fullname: 'Putback shot',
    row: 5,
    column: 11,
    colorClass: 'orangish',
    description:
      'Directly transfer the energy from a missed shot back to the basket without dribbling. Also known as a tip in shot.',
  },

  // row 6
  {
    element: 'Ob',
    shotType: 'Move',
    shotImg: ObFullImg,
    fullname: 'Off-backboard dunk',
    row: 6,
    column: 1,
    colorClass: 'blue',
    description:
      'Discovered by Tracy McGrady, this so called "alley oop to yourself" can cause 13 chain reactions in 33 seconds.',
  },
  {
    element: 'Sb',
    shotType: 'Move',
    fullname: 'Step back shot',
    row: 6,
    column: 2,
    colorClass: 'grey',
    description:
      'One of two exceptionally reactive shots, it reacts to the threat of a defender by stepping back to create space.',
  },
  {
    element: 'J',
    shotType: 'General',
    fullname: 'Jump shot',
    row: 6,
    column: 3,
    colorClass: 'yellow',
    description:
      'Classified as liquid, this shot splashes when it comes in contact with the hoop.',
  },
  {
    element: 'M',
    shotType: 'General',
    shotImg: MFullImg,
    fullname: 'Midrange shot',
    row: 6,
    column: 4,
    colorClass: 'yellow',
    description:
      'Although a dying breed in the current periodic trend, one Raptor still find success with this shot at a high rate.',
  },
  {
    element: 'Po',
    shotType: 'General',
    fullname: 'Post up shot',
    row: 6,
    column: 5,
    colorClass: 'yellow',
    description:
      'A shot frequently used by bigger players. The bigger the mass of the players, the more gravitational pull of the defense it has.',
  },
  {
    element: 'Pu',
    shotType: 'General',
    fullname: 'Pull up shot',
    row: 6,
    column: 6,
    colorClass: 'yellow',
    description:
      'While dribbling, quickly pull up for a shot to take advantage of a defender\'s slow chemical reaction. For those who REALLY hoop, this shot is also known as the "hesi pull up jimbo".',
  },
  {
    element: 'Tk',
    shotType: 'Long range',
    fullname: 'Top of key shot',
    diagram: tkImg,
    row: 6,
    column: 7,
    colorClass: 'green',
    description:
      "This shot's electron is positioned perpendicular to the nucleus baseline as outlined in the diagram below.",
  },
  {
    element: 'W',
    shotType: 'Long range',
    fullname: 'Wing shot',
    diagram: wImg,
    row: 6,
    column: 8,
    colorClass: 'green',
    description:
      "This shot's electron is positioned at a 45° angle to the nucleus baseline as outlined in the diagram below.",
  },
  {
    element: 'Gw',
    shotType: 'Very hard',
    fullname: 'Game winner',
    row: 6,
    column: 9,
    colorClass: 'peachish',
    description:
      'Having successfully undergone the process of alchemy, this shot is money.',
  },
  {
    element: 'Pd',
    shotType: 'Near basket',
    fullname: 'Poster dunk',
    row: 6,
    column: 10,
    colorClass: 'orangish',
    description:
      'A poster dunk is formed as a result of a chemical reaction when a dunk (D) comes in contact with a defender at the rim.',
  },
  {
    element: 'R',
    shotType: 'Near basket',
    fullname: 'Reverse layup',
    row: 6,
    column: 11,
    colorClass: 'orangish',
    description:
      'The reverse layup has been recorded multiple times of moving from one side of the periodic table to the opposite side.',
  },

  // row 7
  {
    element: 'Pf',
    shotType: 'Move',
    shotImg: PfFullImg,
    fullname: 'Pump fake shot',
    row: 7,
    column: 1,
    colorClass: 'blue',
    description:
      'WARNING: Radioactive decay and loss of energy may occur due to repetition. In other words, the more you pump fake, the less effective it is.',
  },
  {
    element: 'T',
    shotType: 'Move',
    fullname: 'Turnaround shot',
    row: 7,
    column: 2,
    colorClass: 'grey',
    description:
      'One of two exceptionally reactive shots, it reacts to the threat of a defender by spinning to create space.',
  },
  {
    element: 'Ru',
    shotType: 'General',
    fullname: 'Running shot',
    row: 7,
    column: 3,
    colorClass: 'yellow',
    description:
      'The Kinetic Theory of Basketball states that the running shot occurs in a rapid motion while releasing the ball.',
  },
  {
    element: 'Sp',
    shotType: 'General',
    fullname: 'Spot up shot',
    row: 7,
    column: 4,
    colorClass: 'yellow',
    description:
      'A conductive shot, it transfers the heat of the ball from the passer directly to the basket without much movement. Also known as a catch and shoot shot (Cs).',
  },
  {
    element: 'Th',
    shotType: 'General',
    fullname: 'Two-handed shot',
    row: 7,
    column: 5,
    colorClass: 'yellow',
    description: 'Held with two hands by players for extra chemical stability.',
  },
  {
    element: 'To',
    fullname: 'Two',
    row: 7,
    column: 6,
    colorClass: 'yellow',
    description:
      'The atom of the basic shot. Its nucleus contains two protons with a positive charge of +2 points.',
    shotType: 'General',
  },
  {
    element: 'Bb',
    shotType: 'WTF',
    fullname: 'Behind the backboard shot',
    row: 7,
    column: 7,
    colorClass: 'grass',
    description:
      'An exceedingly difficult shot to pull off, it dissolves logic when it reaches its melting point.',
  },
  {
    element: 'Bs',
    shotType: 'WTF',
    shotImg: BsFullImg,
    fullname: 'Backboard shattering dunk',
    row: 7,
    column: 8,
    colorClass: 'grass',
    description:
      'This notable dunk (D) was discovered by Darryl Dawkins. It has one unique chemical characteristic; it can explode when exposed to old backboards.',
  },
  {
    element: 'Ff',
    shotType: 'WTF',
    fullname: 'Falling to the floor shot',
    row: 7,
    column: 9,
    colorClass: 'grass',
    description: 'The hardest chemical shot equation to balance.',
  },
  {
    element: 'P',
    shotType: 'WTF',
    fullname: 'Pass shot',
    row: 7,
    column: 10,
    colorClass: 'grass',
    description:
      'This shot was discovered by accident when a shot chemist fortuitously knocked down a bucket when he tried to pass it to a colleague.',
  },
  {
    element: 'Ts',
    shotType: 'WTF',
    fullname: 'Three sixty (360°) shot',
    row: 7,
    column: 11,
    colorClass: 'grass',
    description:
      'The three sixty shot completes a full atomic orbit before releasing the ball.',
  },
];

const bonusElementData = [
  {
    element: 'Bq',
    fullname: "Shaq's BBQ chicken",
    prereqElements: ['D', 'Po'],
    shotType: 'Signature',
    shotImg: BqFullImg,
    description:
      'Any dunk (D) made by Shaq after posting up classifies as "BBQ chicken" as long as it meets the criteria of having a very heavy atomic mass.',
  },
  {
    element: 'Dm',
    fullname: "Hakeem's dream shake",
    prereqElements: ['Po', 'T'],
    shotType: 'Signature',
    shotImg: DmFullImg,
    description:
      'The dream shake has the most complex molecule ever found. Using a combination of lethal footwork and fakes, it shakes off the defender to turn around for an easy shot.',
  },
  {
    element: 'Of',
    fullname: "Dirk's one leg fadeaway",
    prereqElements: ['Fa', 'Ol'],
    shotType: 'Signature',
    shotImg: OfFullImg,
    description:
      'This signature shot was discovered by a German shot chemist and its chemical compound is composed of a fadeaway shot (Fa) and a one-legged shot (Ol).',
  },
  {
    element: 'Pt',
    fullname: "Steph's pull up three",
    prereqElements: ['Pu', 'Te'],
    shotType: 'Signature',
    shotImg: PtFullImg,
    description:
      '"I been Steph Curry with the shot, been cookin\' with the sauce, Chef Curry with the pot full of radioactive energy, boy."',
  },
  {
    element: 'Sj',
    fullname: "MJ's Space Jam dunk",
    prereqElements: ['D', 'Hc'],
    shotType: 'Signature',
    shotImg: SjFullImg,
    description:
      'Strongly bonded by a stretchy element, this signature shot is a Monstar killer.',
  },
  {
    element: 'Sh',
    fullname: "Kareem's skyhook",
    prereqElements: ['H', 'Ol'],
    shotType: 'Signature',
    shotImg: ShFullImg,
    description:
      "The most unstoppable shot in basketball history. Today's players are unable to imitate because it would require a facility larger than the Large Hadron Collider to recreate the nuclear fusion.",
  },
  {
    element: 'Tm',
    fullname: "LeBron's tomahawk",
    prereqElements: ['D', 'Fb'],
    shotType: 'Signature',
    shotImg: TmFullImg,
    description:
      "E=mc²³. (Energy of a tomahawk dunk equals LeBron's mass multiplied by LeBron's speed on a fastbreak to the power of 23).",
  },
  {
    element: 'Uu',
    fullname: "McHale's up and under shot",
    prereqElements: ['Pf', 'Po'],
    shotType: 'Signature',
    shotImg: UuFullImg,
    description:
      'Faking to go up with the ball causes a chain reaction of the defender jumping and the player going under for a wide open shot.',
  },
];

const bonusElements: BonusPeriodicElement[] = map(bonusElementData, el => ({
  ...el,
  colorClass: 'bonus',
  unlocker: false,
}));

const elements: PeriodicElement[] = map(baseElements, el => ({
  ...el,
  unlocker: some(bonusElements, ({ prereqElements }) =>
    includes(prereqElements, el.element),
  ),
}));

const metaElements: PeriodicElement[] = [
  {
    element: '404',
    shotType: 'StatMuse',
    fullname: 'Not Found',
    row: 0,
    column: 0,
    colorClass: 'meta',
    unlocker: false,
    description: `We've discovered 58 shots in the game of basketball and what you're looking for isn't one of them.`,
  },
];

export { elements, bonusElements, metaElements };



// WEBPACK FOOTER //
// ./src/data/index.ts